import { Link } from 'react-router-dom';
import { monthsDataEN, monthsDataUA } from 'constants/moths';
import { EVENT_TYPES, TYPE_LABELS } from 'constants/eventTypes';
import EventTypeIcon from 'common/EventTypeIcon/EventTypeIcon';
import ErrorState from 'common/ErrorState/ErrorState';
import { useCalendar } from 'hooks/useCalendar';
import MonthSection from './MonthSection/MonthSection';
import Loader from 'components/Loader/Loader';
import './Calendar.css';
import { useLanguage } from 'hooks/useLanguage';

/**
 * Календар заходів.
 *
 * Джерел два — змагання з e-phan і заходи з CMS, — але сторінка про це не знає:
 * зведення робить useCalendar (ADR-0012). Знає вона лише про часткову втрату,
 * коли одне з джерел мовчить: показати половину календаря мовчки було б гірше,
 * ніж сказати, що це половина.
 */
export default function Calendar() {
  const language = useLanguage();
  const { data: events, status, error, retry, isLoading, partial } = useCalendar();

  const yearsPairString = `${new Date().getFullYear()} - ${new Date().getFullYear() + 1}`;
  const monthsData = language === 'en' ? monthsDataEN : monthsDataUA;

  const months = monthsData
    .filter((month) => events.map(({ start }) => new Date(start).getMonth()).includes(monthsData.indexOf(month)))
    .filter((month) => monthsData.indexOf(month) >= new Date().getMonth());

  return (
    <article className='calendar section'>
      <div className='container'>
        <header className='calendar-head'>
          <h1 className='page-title'>
            {language === 'en'
              ? `Calendar of events for ${yearsPairString}`
              : `Календар заходів на ${yearsPairString} рік`}
          </h1>
          <p className='lede'>
            {language === 'en'
              ? 'Competitions, meetings and professional training supported by SGOSTU, from the current month onwards.'
              : 'Змагання, збори та професійне навчання за підтримки СГОСТУ, починаючи з поточного місяця.'}
          </p>

          <Link className='btn btn--secondary calendar-archive-link' to='/calendar/archive'>
            {language === 'en' ? 'Past events' : 'Заходи, що відбулися'}
            <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
              <path
                d='M5 12h14M13 6l6 6-6 6'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.6'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </Link>

          {/* Гайд по значках типу заходу з карток нижче — сама іконка на
              банері нічого не пояснює без підпису поруч. */}
          <ul className='calendar-legend'>
            {EVENT_TYPES.map((type) => (
              <li className='calendar-legend-item' key={type}>
                <EventTypeIcon type={type} />
                <span>{TYPE_LABELS[type]?.[language] || type}</span>
              </li>
            ))}
          </ul>
        </header>
      </div>

      {isLoading && <Loader />}

      {status === 'failed' && (
        <div className='container'>
          <ErrorState error={error} onRetry={retry} />
        </div>
      )}

      {status === 'succeeded' && partial && (
        <div className='container'>
          <p className='calendar-partial'>
            {partial === 'competitions'
              ? language === 'en'
                ? 'Competitions from e-phan are temporarily unavailable — the calendar shows only SGOSTU events.'
                : 'Змагання з e-phan тимчасово недоступні — у календарі лише заходи СГОСТУ.'
              : language === 'en'
              ? 'SGOSTU events are temporarily unavailable — the calendar shows only competitions from e-phan.'
              : 'Заходи СГОСТУ тимчасово недоступні — у календарі лише змагання з e-phan.'}
          </p>
        </div>
      )}

      {status === 'succeeded' &&
        (months.length > 0 ? (
          months.map((month) => (
            <MonthSection month={month} monthIdx={monthsData.indexOf(month)} key={month} events={events} />
          ))
        ) : (
          <div className='container'>
            <p className='empty-note'>
              {language === 'en'
                ? 'There are no upcoming events in the calendar yet.'
                : 'Найближчих заходів у календарі поки немає.'}
            </p>
          </div>
        ))}
    </article>
  );
}
